import { useTabActive } from "@/components/useTabActive";
import logger from "@/lib/logger";
import { okstatus } from "@/lib/utils";
import { Task, TaskStatus } from "@/types";
import { TaskSchema } from "@/types/schemas";
import { useCallback, useEffect, useState } from "react";
import { mutate } from "swr";

export const useTaskStatus = (
  taskId?: string,
  period = 500,
  /** Update task data for those keys */ mutations?: string[]
) => {
  const [status, setStatus] = useState<TaskStatus | undefined>();
  // Follow the next task if it exists
  const [nextTaskId, setNextTaskId] = useState<string | undefined>();
  const isActive = useTabActive();
  const update = useCallback((task: Pick<Task, "id" | "status">) => {
    mutate(`/api/task/${task.id}`, task, { revalidate: false });
    setStatus(task.status);
  }, []);

  useEffect(() => {
    let cancelled = false;
    let pollingInterval: ReturnType<typeof setInterval> | undefined = undefined;

    const fetchStatus = async () => {
      if (!isActive) {
        return;
      }

      try {
        const result = taskId
          ? await fetch(`/api/task/${nextTaskId ?? taskId}`)
              .then(okstatus)
              .then(res => res.json())
              .then(json => TaskSchema.parse(json))
          : undefined;

        if (cancelled) {
          return;
        }

        if (result) {
          setStatus(oldStatus => {
            if (oldStatus === result.status) {
              return oldStatus;
            }

            mutations?.map(key => mutate(key, result, { revalidate: false }));
            return result.status;
          });

          if (result.status === "error") {
            clearInterval(pollingInterval);
          } else if (result.status === "completed") {
            if (result.nextTaskId) {
              setNextTaskId(result.nextTaskId);
              logger.info("Following status of the next task", result.nextTaskId);
            } else {
              clearInterval(pollingInterval);
            }
          }
        }
      } catch (error) {
        if (cancelled) {
          return;
        }
        logger.warn("Error fetching task status, taskId", taskId, "error", error);
        clearInterval(pollingInterval);
      }
    };

    pollingInterval = setInterval(fetchStatus, period ?? 5000);

    // Initial fetch to get the task status immediately
    fetchStatus();

    // Cleanup function to clear the interval on component unmount
    return () => {
      cancelled = true;
      clearInterval(pollingInterval);
    };
  }, [period, taskId, nextTaskId, isActive, mutations]);

  useEffect(() => {
    if (taskId) {
      setNextTaskId(undefined);
    }
  }, [taskId]);

  return {
    status,
    active: status === "active",
    queued: status === "queued",
    update
  };
};
